<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  entries: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  meta: {
    type: Object,
    default: () => ({ page: 1, per_page: 25, total: 0 }),
  },
  collections: {
    type: Array,
    default: () => [],
  },
  selectedCollection: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  'delete',
  'bulk-delete',
  'page-change',
  'search',
  'filter-collection',
]);

const searchQuery = ref('');
const selectedIds = ref([]);
const selectAll = ref(false);
const expandedId = ref(null);

const totalPages = computed(() =>
  Math.ceil(props.meta.total / props.meta.per_page)
);
const hasSelection = computed(() => selectedIds.value.length > 0);
const allSelected = computed(
  () =>
    props.entries.length > 0 &&
    selectedIds.value.length === props.entries.length
);

watch(selectAll, val => {
  if (val) {
    selectedIds.value = props.entries.map(e => e.id);
  } else if (allSelected.value) {
    selectedIds.value = [];
  }
});

watch(
  () => props.entries,
  () => {
    selectedIds.value = [];
    selectAll.value = false;
    expandedId.value = null;
  }
);

const toggleSelect = id => {
  const index = selectedIds.value.indexOf(id);
  if (index > -1) {
    selectedIds.value.splice(index, 1);
  } else {
    selectedIds.value.push(id);
  }
};

const toggleExpand = id => {
  expandedId.value = expandedId.value === id ? null : id;
};

const handleSearch = () => {
  emit('search', searchQuery.value);
};

const handleClearSearch = () => {
  searchQuery.value = '';
  emit('search', '');
};

const handleBulkDelete = () => {
  if (selectedIds.value.length > 0) {
    emit('bulk-delete', { ids: selectedIds.value });
  }
};


const truncate = (text, length = 100) => {
  if (!text) return '';
  return text.length > length ? text.slice(0, length) + '...' : text;
};

const formatDate = dateString => {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const getMetadata = entry => {
  const data = entry.data || {};
  // For api_record entries, return all fields except internal ones
  if (data.type === 'api_record') {
    return Object.fromEntries(
      Object.entries(data).filter(([key]) => !['type', 'source_id'].includes(key))
    );
  }
  // For file-based entries, show the standard subset
  const meta = {};
  if (data.sheet) meta.Sheet = data.sheet;
  if (data.row_number) meta.Row = data.row_number;
  if (data.type) meta.Type = data.type;
  if (data.total_rows) meta['Total Rows'] = data.total_rows;
  return meta;
};

const paginationPages = computed(() => {
  const current = props.meta.page;
  const total = totalPages.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = [];
  pages.push(1);
  if (current > 3) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('...');
  if (total > 1) pages.push(total);

  return pages;
});
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Toolbar -->
    <div
      class="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30"
    >
      <!-- Search -->
      <div class="relative flex-1 min-w-[200px] max-w-sm">
        <span
          class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5"
        />
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('OMNI.DATA_SOURCES.SEARCH_PLACEHOLDER')"
          class="w-full pl-8 pr-8 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-all"
          @keyup.enter="handleSearch"
        />
        <button
          v-if="searchQuery"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          @click="handleClearSearch"
        >
          <span class="i-lucide-x size-3.5" />
        </button>
      </div>

      <!-- Collection Filter -->
      <div class="relative min-w-[150px]">
        <select
          :value="selectedCollection || ''"
          class="w-full appearance-none bg-none px-3 py-2 pr-9 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 transition-all"
          @change="emit('filter-collection', $event.target.value || null)"
        >
          <option value="">
            {{ t('OMNI.DATA_SOURCES.ALL_COLLECTIONS') }}
          </option>
          <option
            v-for="col in collections"
            :key="col"
            :value="col"
          >
            {{ col }}
          </option>
        </select>
        <span class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 w-4 h-4" />
      </div>

      <!-- Bulk Actions -->
      <div
        v-if="hasSelection"
        class="flex items-center gap-2 ml-auto"
      >
        <span class="text-xs text-slate-500 dark:text-slate-400">
          {{ selectedIds.length }} {{ t('OMNI.DATA_SOURCES.SELECTED') }}
        </span>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
          @click="handleBulkDelete"
        >
          <span class="i-lucide-trash-2 w-3 h-3" />
          {{ t('OMNI.DATA_SOURCES.DELETE_SELECTED') }}
        </button>
      </div>

      <div
        v-if="!hasSelection"
        class="text-xs text-slate-400 ml-auto"
      >
        {{ meta.total }} {{ t('OMNI.DATA_SOURCES.ENTRIES') }}
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto">
      <div
        v-if="loading"
        class="flex items-center justify-center h-full"
      >
        <span class="i-lucide-loader-2 w-8 h-8 animate-spin text-woot-500" />
      </div>

      <div
        v-else-if="entries.length === 0"
        class="flex flex-col items-center justify-center h-full text-center p-8"
      >
        <div
          class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 mb-3"
        >
          <span
            class="i-lucide-inbox text-2xl text-slate-400 dark:text-slate-500"
          />
        </div>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('OMNI.DATA_SOURCES.NO_ENTRIES') }}
        </p>
      </div>

      <!-- Table -->
      <table
        v-else
        class="w-full"
      >
        <thead class="bg-slate-50/80 dark:bg-slate-800/50 sticky top-0 z-10">
          <tr>
            <th class="px-4 py-2.5 w-10">
              <input
                v-model="selectAll"
                type="checkbox"
                class="rounded border-slate-300 dark:border-slate-600 text-woot-500 focus:ring-woot-500"
              />
            </th>
            <th
              class="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider"
            >
              {{ t('OMNI.DATA_SOURCES.CONTENT') }}
            </th>
            <th
              class="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-32"
            >
              {{ t('OMNI.DATA_SOURCES.COLLECTION') }}
            </th>
            <th
              class="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-36"
            >
              {{ t('OMNI.DATA_SOURCES.SOURCE') }}
            </th>
            <th
              class="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-24"
            >
              {{ t('OMNI.DATA_SOURCES.EMBEDDING') }}
            </th>
            <th
              class="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider w-24"
            >
              {{ t('OMNI.DATA_SOURCES.DATE') }}
            </th>
            <th class="px-4 py-2.5 w-20" />
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-50 dark:divide-slate-800">
          <template
            v-for="entry in entries"
            :key="entry.id"
          >
            <!-- Main Row -->
            <tr
              class="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 cursor-pointer transition-colors"
              :class="{
                'bg-woot-50/30 dark:bg-woot-900/5':
                  selectedIds.includes(entry.id),
                'bg-slate-50/50 dark:bg-slate-800/20':
                  expandedId === entry.id,
              }"
              @click="toggleExpand(entry.id)"
            >
              <td
                class="px-4 py-3"
                @click.stop
              >
                <input
                  type="checkbox"
                  :checked="selectedIds.includes(entry.id)"
                  class="rounded border-slate-300 dark:border-slate-600 text-woot-500 focus:ring-woot-500"
                  @change="toggleSelect(entry.id)"
                />
              </td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <span
                    class="i-lucide-chevron-right w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0"
                    :class="{ 'rotate-90': expandedId === entry.id }"
                  />
                  <p
                    class="text-sm text-slate-800 dark:text-slate-200 line-clamp-2"
                    :title="entry.content_text"
                  >
                    {{ truncate(entry.content_text, 120) }}
                  </p>
                </div>
              </td>
              <td class="px-4 py-3">
                <span
                  v-if="entry.collection"
                  class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                >
                  {{ entry.collection }}
                </span>
                <span
                  v-else
                  class="text-slate-400 text-xs"
                >-</span>
              </td>
              <td class="px-4 py-3">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ entry.source_reference || '-' }}
                </span>
              </td>
              <td class="px-4 py-3">
                <span
                  v-if="entry.has_embedding"
                  class="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"
                >
                  <span class="i-lucide-check-circle w-3.5 h-3.5" />
                  <span class="text-xs font-medium">OK</span>
                </span>
                <span
                  v-else
                  class="inline-flex items-center gap-1 text-amber-500"
                >
                  <span class="i-lucide-clock w-3.5 h-3.5" />
                  <span class="text-xs">{{
                    t('OMNI.DATA_SOURCES.PENDING')
                  }}</span>
                </span>
              </td>
              <td class="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                {{ formatDate(entry.created_at) }}
              </td>
              <td
                class="px-4 py-3"
                @click.stop
              >
                <button
                  class="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                  :title="t('OMNI.DATA_SOURCES.DELETE')"
                  @click="emit('delete', entry.id)"
                >
                  <span class="i-lucide-trash-2 w-3.5 h-3.5" />
                </button>
              </td>
            </tr>

            <!-- Expanded Detail Row -->
            <tr v-if="expandedId === entry.id">
              <td
                colspan="7"
                class="px-0 py-0"
              >
                <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 space-y-4">
                  <!-- Data fields grid (for api_record entries: show all extracted fields) -->
                  <div v-if="Object.keys(getMetadata(entry)).length > 0">
                    <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 flex items-center gap-1.5">
                      <span class="i-lucide-database w-3 h-3" />
                      {{ t('OMNI.DATA_SOURCES.METADATA') }}
                    </h4>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      <div
                        v-for="(value, key) in getMetadata(entry)"
                        :key="key"
                        class="flex flex-col gap-0.5 px-3 py-2 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
                      >
                        <span class="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{{ key }}</span>
                        <span class="text-xs text-slate-700 dark:text-slate-300 break-words">{{ value ?? '—' }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Full content_text -->
                  <div>
                    <h4 class="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 flex items-center gap-1.5">
                      <span class="i-lucide-file-text w-3 h-3" />
                      {{ t('OMNI.DATA_SOURCES.CONTENT') }}
                    </h4>
                    <div class="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap max-h-48 overflow-y-auto font-mono leading-relaxed">
                      {{ entry.content_text || '—' }}
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="totalPages > 1"
      class="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30"
    >
      <div class="text-xs text-slate-500 dark:text-slate-400">
        {{
          t('OMNI.DATA_SOURCES.PAGE_INFO', {
            page: meta.page,
            total: totalPages,
            count: meta.total,
          })
        }}
      </div>
      <div class="flex gap-1">
        <button
          class="px-2.5 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          :disabled="meta.page <= 1"
          @click="emit('page-change', meta.page - 1)"
        >
          <span class="i-lucide-chevron-left w-3.5 h-3.5" />
        </button>
        <template
          v-for="p in paginationPages"
          :key="p"
        >
          <span
            v-if="p === '...'"
            class="px-2 py-1.5 text-xs text-slate-400"
          >...</span>
          <button
            v-else
            class="px-2.5 py-1.5 text-xs border rounded-lg transition-colors"
            :class="
              p === meta.page
                ? 'border-woot-500 bg-woot-500 text-white'
                : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
            "
            @click="emit('page-change', p)"
          >
            {{ p }}
          </button>
        </template>
        <button
          class="px-2.5 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          :disabled="meta.page >= totalPages"
          @click="emit('page-change', meta.page + 1)"
        >
          <span class="i-lucide-chevron-right w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  </div>
</template>
